import { Box } from "@mui/material"
import { GenericWrapperContext, Icon, Typography } from "cakemail-ui-components-v2"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { CustomerContext } from "../../contexts"
import { IPrice } from "../../interfaces/IPrice"
import { FeatureDefinitions } from "../../models/FeatureDefinitions"
import { Messages } from "../../models/Messages"
import { EFeatureCategory } from "../../types/EFeatureCategory"
import { FullFeatureSet } from "../FullFeatureSet"
import { applyBrandFeatureDisplay, featureLabelForBrand, isFeatureHiddenForBrand } from "../lib/brandFeatureDisplay"
import { categoryHasVisibleFeatures } from "../lib/categoryHasVisibleFeatures"
import { overrideLimitsDisplay } from "../lib/overrideLimitsDisplay"
import './styles.scss'


export default function FeatureTableData({ price, category }: { price: IPrice, category: EFeatureCategory }) {
    const [fullFeatureSet, setFullFeatureSet] = useState<any>()
    const [featuresOnlyInPremium, setFeaturesOnlyInPremium] = useState<any>()
    const [currentlyClickedFeature, setCurrentlyClickedFeature] = useState<any>({
        featureName: "",
        planType: ""
    })
    const { t: translate } = useTranslation()
    const customerContext = useContext(CustomerContext)
    const genericWrapperContext = useContext(GenericWrapperContext)

    useEffect(() => {
        Messages.sendHeightToParent()
    }, [currentlyClickedFeature])



    useEffect(() => {
        if (price) {
            let tempFeatureSet = new FullFeatureSet(price)
            if (price?.metadata?.category === "premium") {
                tempFeatureSet.articles_and_events_content_addons = true
                tempFeatureSet.exit_intent = true
                tempFeatureSet.time_based = true
                tempFeatureSet.audience_triggering = true
                tempFeatureSet.drag_and_drop_landing_page = true
                tempFeatureSet.professional_templates_landing_page = true
                tempFeatureSet.dedicated_sending_ip_option = true
                tempFeatureSet.priority_phone_support = true
                tempFeatureSet.ongoing_email_support = true
                tempFeatureSet.professional_templates = true
                tempFeatureSet.insert_reseller_logo = true
            }
            if (price?.metadata?.category === "growth") {
                tempFeatureSet.articles_and_events_content_addons = true
                tempFeatureSet.ongoing_email_support = true
                tempFeatureSet.professional_templates = true
                tempFeatureSet.insert_reseller_logo = true
            }
            if (price?.metadata?.category === "free" && (customerContext?.brand.partnerUrl === "app.courrielleur.com" || customerContext?.brand.partnerUrl === "app.cakemail.com")) {
                tempFeatureSet.drip_campaigns = false
            }
            let premiumProduct = customerContext?.products[0]?.prices?.find((price) => {
                return price?.metadata?.category === "premium"
            })
            if (premiumProduct?.metadata?.usage_limits) {
                premiumProduct.metadata.usage_limits.dedicated_sending_ip_option = true
                premiumProduct.metadata.usage_limits.priority_phone_support = true
                premiumProduct.metadata.usage_limits.professional_templates = true
            }
            const keysFoundFromPremiumPlan = Object.keys(premiumProduct?.metadata?.usage_limits)
            const keysWithPlanAndHardCoded = Object.keys(tempFeatureSet.getFeaturesAndLimits())
            const mergedKeys = [...keysFoundFromPremiumPlan, ...keysWithPlanAndHardCoded].filter((key, index, array) => array.indexOf(key) === index)

            applyBrandFeatureDisplay(customerContext?.brand.partnerUrl, tempFeatureSet, price?.metadata?.category)
            overrideLimitsDisplay(price?.metadata?.category, tempFeatureSet)
            setFeaturesOnlyInPremium((mergedKeys))
            setFullFeatureSet(tempFeatureSet.getFeaturesAndLimits())
        }
    }, [price, customerContext?.brand.partnerUrl, customerContext?.products])

    const configFeatures: string[] = genericWrapperContext?.partnerBrand?.config?.features ?? []
    const hasVisibleFeatures = featuresOnlyInPremium && categoryHasVisibleFeatures(category, configFeatures)

    if (!hasVisibleFeatures) return null

    return (
        <>
            <div>
                <div key={category}>
                    <Typography className="category" sx={{ textTransform: "none" }} variant="h3S">
                        {translate("category-" + category)}
                    </Typography>
                    {featuresOnlyInPremium && Object.keys(FeatureDefinitions).map((feature) => {
                        if (FeatureDefinitions[feature]?.category === category) {
                            if (isFeatureHiddenForBrand(customerContext?.brand.partnerUrl, feature)) {
                                return null
                            }
                            if (FeatureDefinitions[feature]?.brandSpecific && !configFeatures.includes(feature)) {
                                return null
                            }
                            if (FeatureDefinitions[feature]?.displayCardOnly) {
                                return null
                            }

                            const isClicked = currentlyClickedFeature.featureName === feature && currentlyClickedFeature.planType === price?.metadata?.category

                            return (
                                <div
                                    key={feature}
                                    className="icon-container"
                                    onClick={() => {
                                        if (isClicked) {
                                            setCurrentlyClickedFeature({ featureName: "", planType: "" })
                                        } else {
                                            setCurrentlyClickedFeature({ featureName: feature, planType: price?.metadata?.category })
                                        }
                                    }}
                                >
                                    {FeatureDefinitions[feature]?.label && (
                                        <Box className="icon-text-box">
                                            {fullFeatureSet[feature] ? (
                                                <>
                                                    <Icon className="small-icon" name="Checkmark14" color="secondary" />
                                                    <Typography className="billing-underline_label" variant="body1L">
                                                        {translate("feature-" + featureLabelForBrand(customerContext?.brand.partnerUrl, feature))}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Icon className="small-icon" name="Remove14" color="body2" />
                                                    <Typography className="billing-underline_label" color="body2.main" variant="body1L">
                                                        {translate("feature-" + featureLabelForBrand(customerContext?.brand.partnerUrl, feature))}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    )}
                                    {/* Render description directly below the item */}
                                    {isClicked && (
                                        <Box className="feature-description-2">
                                            <Typography
                                                variant="body1L"
                                            >
                                                {translate(`feature-${featureLabelForBrand(customerContext?.brand.partnerUrl, feature)}-description`)}                                            </Typography>
                                        </Box>
                                    )}
                                </div>
                            )
                        }
                        else return null
                    })}
                </div>
                <br />
            </div>
        </>
    )
}
